import { forwardRef } from "react";
import PropTypes from "prop-types";
import Box from "@mui/material/Box";
import MenuItem from "@mui/material/MenuItem";
import GrTextField from "./GrTextField";
import { gr } from "../tokens";

const menuPaperSx = {
  mt: gr.space[1],
  borderRadius: gr.radius.tile,
  border: `1px solid ${gr.color.line}`,
  boxShadow: gr.shadow.raised,
  backgroundImage: "none",
  "& .MuiList-root": { padding: gr.space[1] },
  "& .MuiMenuItem-root": {
    minHeight: gr.control.md,
    borderRadius: gr.radius.chip,
    fontFamily: gr.font.family,
    fontSize: gr.font.size.md,
    color: gr.color.ink,
  },
  "& .MuiMenuItem-root:hover": { background: gr.color.tint },
  "& .MuiMenuItem-root.Mui-selected": {
    background: gr.color.brandSoft,
    color: gr.color.brandDark,
  },
  "& .MuiMenuItem-root.Mui-selected:hover": { background: gr.color.brandSoft },
};

// Compact filter (card headers): 40px, no floating label; the label sits inside as a muted prefix ("Programme: All").
const compactSx = {
  "& .MuiInputBase-input:not(textarea)": {
    py: `calc((${gr.control.md} - 22px) / 2)`,
    pl: gr.space[3],
    display: "flex",
    gap: gr.space[1],
    minWidth: 0,
  },
};

// Select field: same look as GrTextField; options = [{ value, label }].
// compact: filter style for card headers (needs options and a label, used as prefix + accessible name).
const GrSelect = forwardRef(function GrSelect(
  {
    options = [],
    SelectProps,
    children,
    compact = false,
    label,
    sx,
    inputProps,
    ...rest
  },
  ref
) {
  const compactProps = compact
    ? {
        renderValue: (v) => {
          const o = options.find((x) => x.value === v);
          return (
            <>
              <Box
                component="span"
                sx={{ color: gr.color.muted, flex: "none" }}
              >
                {label}:
              </Box>
              <Box
                component="span"
                title={
                  typeof (o ? o.label : v) === "string"
                    ? o
                      ? o.label
                      : v
                    : undefined
                }
                sx={{
                  minWidth: 0,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                {o ? o.label : v}
              </Box>
            </>
          );
        },
      }
    : {};
  return (
    <GrTextField
      ref={ref}
      select
      label={compact ? undefined : label}
      inputProps={compact ? { "aria-label": label, ...inputProps } : inputProps}
      sx={compact ? { ...compactSx, ...sx } : sx}
      SelectProps={{
        ...compactProps,
        ...SelectProps,
        MenuProps: {
          PaperProps: { sx: menuPaperSx },
          ...(SelectProps && SelectProps.MenuProps),
        },
      }}
      {...rest}
    >
      {children ||
        options.map((o) => (
          <MenuItem key={o.value} value={o.value} disabled={o.disabled}>
            {o.label}
          </MenuItem>
        ))}
    </GrTextField>
  );
});

GrSelect.propTypes = {
  options: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
        .isRequired,
      label: PropTypes.node.isRequired,
      disabled: PropTypes.bool,
    })
  ),
  SelectProps: PropTypes.object,
  children: PropTypes.node,
  compact: PropTypes.bool,
  label: PropTypes.node,
  sx: PropTypes.object,
  inputProps: PropTypes.object,
};

export default GrSelect;
